import { CRUDHelper } from '../utils/crudHelpers';
import { User } from 'firebase/auth';
import type {
  Empresa,
  EquipmentBrand,
  SolidsDischargeType,
  ScreenType,
  Operadora,
  Equipo,
  Pad,
  Pozo,
  Tramo,
  Trepano,
  PiletaTipo,
  PiletaEquipo,
  Material,
  ServicioTipo,
  LookupTable,
  DailyActivity,
  MudType,
  PhaseOleosa,
  Densificante,
  DensidadLGS,
  ContainerMeasures
} from '../types';

export const useCRUDOperations = (user: User | null | undefined) => {
  // ===== Equipment Brand CRUD Operations =====
  const handleEquipmentBrandSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('equipmentBrands', {
      equipmentType: data.equipmentType,
      brand: data.brand,
      model: data.model || '',
      active: true
    }, user?.uid, user?.email || '');
  };

  const handleEquipmentBrandToggle = async (item: EquipmentBrand) => {
    await CRUDHelper.toggleBoolean(
      'equipmentBrands',
      item.id,
      'active',
      item.active,
      user?.uid,
      user?.email || ''
    );
  };

  const handleEquipmentBrandDelete = async (item: EquipmentBrand) => {
    if (window.confirm(`¿Estás seguro de eliminar la marca "${item.brand}"?`)) {
      await CRUDHelper.delete('equipmentBrands', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleEquipmentBrandEdit = async (item: EquipmentBrand, key: string, newValue: any) => {
    await CRUDHelper.update('equipmentBrands', item.id, { [key]: newValue }, user?.uid, user?.email || '');
  };

  // ===== Solids Discharge Type CRUD Operations =====
  const handleDischargeTypeSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('solidsDischargeTypes', {
      type: data.type,
      description: data.description || '',
      active: true
    }, user?.uid, user?.email || '');
  };

  const handleDischargeTypeToggle = async (item: SolidsDischargeType) => {
    await CRUDHelper.toggleBoolean(
      'solidsDischargeTypes',
      item.id,
      'active',
      item.active,
      user?.uid,
      user?.email || ''
    );
  };

  const handleDischargeTypeDelete = async (item: SolidsDischargeType) => {
    if (window.confirm(`¿Estás seguro de eliminar el tipo "${item.type}"?`)) {
      await CRUDHelper.delete('solidsDischargeTypes', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleDischargeTypeEdit = async (item: SolidsDischargeType, key: string, newValue: any) => {
    await CRUDHelper.update('solidsDischargeTypes', item.id, { [key]: newValue }, user?.uid, user?.email || '');
  };

  // ===== Screen Type CRUD Operations =====
  const handleScreenTypeSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('screenTypes', {
      mesh: Number(data.mesh),
      description: data.description,
      equipmentApplicable: ['zaranda1', 'zaranda2', 'zaranda3'], // Default to all zarandas
      active: true
    }, user?.uid, user?.email || '');
  };

  const handleScreenTypeToggle = async (item: ScreenType) => {
    await CRUDHelper.toggleBoolean(
      'screenTypes',
      item.id,
      'active',
      item.active,
      user?.uid,
      user?.email || ''
    );
  };

  const handleScreenTypeDelete = async (item: ScreenType) => {
    if (window.confirm(`¿Estás seguro de eliminar la malla "${item.mesh} mesh"?`)) {
      await CRUDHelper.delete('screenTypes', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleScreenTypeEdit = async (item: ScreenType, key: string, newValue: any) => {
    const value = key === 'mesh' ? Number(newValue) : newValue;
    await CRUDHelper.update('screenTypes', item.id, { [key]: value }, user?.uid, user?.email || '');
  };

  // ===== Empresa CRUD Operations =====
  const handleEmpresaSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('empresas', {
      nombre: data.nombre,
      codigo: data.codigo.toLowerCase(), // Ensure lowercase for consistency
      active: true
    }, user?.uid, user?.email || '');
  };

  const handleEmpresaToggle = async (item: Empresa) => {
    await CRUDHelper.toggleBoolean(
      'empresas',
      item.id,
      'active',
      item.active,
      user?.uid,
      user?.email || ''
    );
  };

  const handleEmpresaDelete = async (item: Empresa) => {
    if (window.confirm(`¿Estás seguro de eliminar la empresa "${item.nombre}"?`)) {
      await CRUDHelper.delete('empresas', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleEmpresaEdit = async (item: Empresa, key: string, newValue: any) => {
    const value = key === 'codigo' ? newValue.toLowerCase() : newValue;
    await CRUDHelper.update('empresas', item.id, { [key]: value }, user?.uid, user?.email || '');
  };

  // ===== Operadora CRUD Operations =====
  const handleOperadoraSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('operadoras', {
      nombre: data.nombre,
      color: data.color || '#56B046',
      habilitada: true
    }, user?.uid, user?.email || '');
  };

  const handleOperadoraToggle = async (item: Operadora) => {
    await CRUDHelper.toggleBoolean(
      'operadoras',
      item.id,
      'habilitada',
      item.habilitada,
      user?.uid,
      user?.email || ''
    );
  };

  const handleOperadoraDelete = async (item: Operadora) => {
    if (window.confirm(`¿Estás seguro de eliminar la operadora "${item.nombre}"?`)) {
      await CRUDHelper.delete('operadoras', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleOperadoraEdit = async (item: Operadora, key: string, newValue: any) => {
    await CRUDHelper.update('operadoras', item.id, { [key]: newValue }, user?.uid, user?.email || '');
  };

  // ===== Equipo CRUD Operations =====
  const handleEquipoSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('equipos', {
      nombre: data.nombre,
      operadoraId: data.operadoraId,
      habilitado: true
    }, user?.uid, user?.email || '');
  };

  const handleEquipoToggle = async (item: Equipo) => {
    await CRUDHelper.toggleBoolean(
      'equipos',
      item.id,
      'habilitado',
      item.habilitado,
      user?.uid,
      user?.email || ''
    );
  };

  const handleEquipoDelete = async (item: Equipo) => {
    if (window.confirm(`¿Estás seguro de eliminar el equipo "${item.nombre}"?`)) {
      await CRUDHelper.delete('equipos', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleEquipoEdit = async (item: Equipo, key: string, newValue: any) => {
    await CRUDHelper.update('equipos', item.id, { [key]: newValue }, user?.uid, user?.email || '');
  };

  // ===== Pad CRUD Operations =====
  const handlePadSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('pads', {
      nombre: data.nombre,
      operadoraId: data.operadoraId,
      equipoId: data.equipoId,
      activo: true
    }, user?.uid, user?.email || '');
  };

  const handlePadDelete = async (item: Pad) => {
    if (window.confirm(`¿Estás seguro de eliminar el pad "${item.nombre}"?`)) {
      await CRUDHelper.delete('pads', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handlePadEdit = async (item: Pad, key: string, newValue: any) => {
    await CRUDHelper.update('pads', item.id, { [key]: newValue }, user?.uid, user?.email || '');
  };

  // ===== Pozo CRUD Operations =====
  const handlePozoSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('pozos', {
      nombre: data.nombre,
      padId: data.padId,
      activo: true
    }, user?.uid, user?.email || '');
  };

  const handlePozoDelete = async (item: Pozo) => {
    if (window.confirm(`¿Estás seguro de eliminar el pozo "${item.nombre}"?`)) {
      await CRUDHelper.delete('pozos', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handlePozoEdit = async (item: Pozo, key: string, newValue: any) => {
    await CRUDHelper.update('pozos', item.id, { [key]: newValue }, user?.uid, user?.email || '');
  };

  // ===== Tramo CRUD Operations =====
  const handleTramoSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('tramos', {
      fase: data.fase,
      tamanioTrepan: data.tamanioTrepan,
      profundidadObjetivo: Number(data.profundidadObjetivo),
      pozoId: data.pozoId,
      activo: true
    }, user?.uid, user?.email || '');
  };

  const handleTramoDelete = async (item: Tramo) => {
    if (window.confirm(`¿Estás seguro de eliminar el tramo "${item.fase}"?`)) {
      await CRUDHelper.delete('tramos', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleTramoEdit = async (item: Tramo, key: string, newValue: any) => {
    const value = key === 'profundidadObjetivo' ? Number(newValue) : newValue;
    await CRUDHelper.update('tramos', item.id, { [key]: value }, user?.uid, user?.email || '');
  };

  // ===== Trepano CRUD Operations =====
  const handleTrepanoSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('trepanos', {
      nombre: data.nombre,
      tamanio: data.tamanio,
      activo: true
    }, user?.uid, user?.email || '');
  };

  const handleTrepanoToggle = async (item: Trepano) => {
    await CRUDHelper.toggleBoolean(
      'trepanos',
      item.id,
      'activo',
      item.activo,
      user?.uid,
      user?.email || ''
    );
  };

  const handleTrepanoDelete = async (item: Trepano) => {
    if (window.confirm(`¿Estás seguro de eliminar el trepano "${item.nombre}"?`)) {
      await CRUDHelper.delete('trepanos', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleTrepanoEdit = async (item: Trepano, key: string, newValue: any) => {
    await CRUDHelper.update('trepanos', item.id, { [key]: newValue }, user?.uid, user?.email || '');
  };

  // ===== Pileta Tipo CRUD Operations =====
  const handlePiletaTipoSubmit = async (data: Record<string, any>) => {
    const tabla = String(data.tabla || '').trim().replace(/\.csv$/i, '');
    await CRUDHelper.create('piletasTipos', {
      nombre: data.nombre,
      tabla,
      activo: true
    }, user?.uid, user?.email || '');
  };

  const handlePiletaTipoDelete = async (item: PiletaTipo) => {
    if (window.confirm(`¿Estás seguro de eliminar el tipo de pileta "${item.nombre}"?`)) {
      await CRUDHelper.delete('piletasTipos', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handlePiletaTipoEdit = async (item: PiletaTipo, key: string, newValue: any) => {
    const normalizedValue = key === 'tabla'
      ? String(newValue || '').trim().replace(/\.csv$/i, '')
      : newValue;
    await CRUDHelper.update('piletasTipos', item.id, { [key]: normalizedValue }, user?.uid, user?.email || '');
  };

  // ===== Pileta Equipo CRUD Operations =====
  const handlePiletaEquipoSubmit = async (data: Record<string, any>) => {
    // Generate unique piletaId (short random ID like Firebase does)
    const generatePiletaId = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < 20; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    };

    const piletaId = generatePiletaId();
    const docId = `${data.equipoId}_${piletaId}`;

    const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('../firebase');

    // Create pileta document with custom ID: equipoId_piletaId
    await setDoc(doc(db, 'piletas', docId), {
      piletaId: piletaId,
      nombre: data.nombre,
      serial: data.serial,
      tipoId: data.tipoId,
      equipoId: data.equipoId,
      habilitada: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Initialize nivel to 0 with same document ID
    await setDoc(doc(db, 'niveles', docId), {
      equipoId: data.equipoId,
      piletaId: piletaId,
      nivelActual: 0,
      operadoraId: '', // Will be set by the app when first used
      lastUpdatedBy: 'inicializada',
      ultimaActualizacion: serverTimestamp()
    });
  };

  const handlePiletaEquipoToggle = async (item: PiletaEquipo) => {
    await CRUDHelper.toggleBoolean(
      'piletas',
      item.id,
      'habilitada',
      item.habilitada,
      user?.uid,
      user?.email || ''
    );
  };

  const handlePiletaEquipoDelete = async (item: PiletaEquipo) => {
    if (window.confirm(`¿Estás seguro de eliminar la pileta "${item.nombre}"?`)) {
      await CRUDHelper.delete('piletas', item.id, user?.uid, user?.email || '', item);
      const { doc, deleteDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      await deleteDoc(doc(db, 'niveles', item.id)).catch(() => {});
    }
  };

  const handlePiletaEquipoEdit = async (item: PiletaEquipo, key: string, newValue: any) => {
    await CRUDHelper.update('piletas', item.id, { [key]: newValue }, user?.uid, user?.email || '');
  };

  // ===== Material CRUD Operations =====
  const handleMaterialSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('materiales', {
      codigoMarbar: data.codigoMarbar,
      nombreComercial: data.nombreComercial,
      unidad: data.unidad,
      costoUnitario: Number(data.costoUnitario),
      categoria: data.categoria,
      activo: true
    }, user?.uid, user?.email || '');
  };

  const handleMaterialToggle = async (item: Material) => {
    await CRUDHelper.toggleBoolean(
      'materiales',
      item.id,
      'activo',
      item.activo,
      user?.uid,
      user?.email || ''
    );
  };

  const handleMaterialDelete = async (item: Material) => {
    if (window.confirm(`¿Estás seguro de eliminar el material "${item.nombreComercial}"?`)) {
      await CRUDHelper.delete('materiales', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleMaterialEdit = async (item: Material, key: string, newValue: any) => {
    const value = key === 'costoUnitario' ? Number(newValue) : newValue;
    await CRUDHelper.update('materiales', item.id, { [key]: value }, user?.uid, user?.email || '');
  };

  // ===== Servicio Tipo CRUD Operations =====
  const handleServicioTipoSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('serviciosTipo', {
      descripcion: data.descripcion,
      costoUnitario: Number(data.costoUnitario),
      unidadCobro: data.unidadCobro,
      codigoMarbar: data.codigoMarbar || null,
      item: data.item || null,
      subItem: data.subItem || null,
      activo: true
    }, user?.uid, user?.email || '');
  };

  const handleServicioTipoToggle = async (item: ServicioTipo) => {
    await CRUDHelper.toggleBoolean(
      'serviciosTipo',
      item.id,
      'activo',
      item.activo,
      user?.uid,
      user?.email || ''
    );
  };

  const handleServicioTipoDelete = async (item: ServicioTipo) => {
    if (window.confirm(`¿Estás seguro de eliminar el servicio "${item.descripcion}"?`)) {
      await CRUDHelper.delete('serviciosTipo', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleServicioTipoEdit = async (item: ServicioTipo, key: string, newValue: any) => {
    const value = key === 'costoUnitario' ? Number(newValue) : newValue;
    await CRUDHelper.update('serviciosTipo', item.id, { [key]: value }, user?.uid, user?.email || '');
  };

  // ===== Daily Activity CRUD Operations =====
  const handleDailyActivitySubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('dailyActivities', {
      name: data.name,
      isActive: true,
      displayName: data.name,
      activity: data.name,
      category: 'drilling',
      order: Date.now(), // Use timestamp for ordering
      enablesDataEntry: true
    }, user?.uid, user?.email || '');
  };

  const handleDailyActivityToggle = async (item: DailyActivity) => {
    await CRUDHelper.toggleBoolean(
      'dailyActivities',
      item.id,
      'isActive',
      item.isActive,
      user?.uid,
      user?.email || ''
    );
  };

  const handleDailyActivityDelete = async (item: DailyActivity) => {
    if (window.confirm(`¿Estás seguro de eliminar la actividad "${item.name}"?`)) {
      await CRUDHelper.delete('dailyActivities', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleDailyActivityEdit = async (item: DailyActivity, key: string, newValue: any) => {
    await CRUDHelper.update('dailyActivities', item.id, { [key]: newValue }, user?.uid, user?.email || '');
  };

  // ===== Mud Type CRUD Operations =====
  const handleMudTypeSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('mudTypes', {
      type: data.type,
      displayName: data.displayName,  // Fixed: added missing displayName field
      // Removed baseMudType - not in form, use type instead
      description: data.description || '',
      density: data.density ? Number(data.density) : undefined,
      active: true
    }, user?.uid, user?.email || '');
  };

  const handleMudTypeToggle = async (item: MudType) => {
    await CRUDHelper.toggleBoolean(
      'mudTypes',
      item.id,
      'active',
      item.active,
      user?.uid,
      user?.email || ''
    );
  };

  const handleMudTypeDelete = async (item: MudType) => {
    if (window.confirm(`¿Estás seguro de eliminar el tipo de lodo "${item.type}"?`)) {
      await CRUDHelper.delete('mudTypes', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleMudTypeEdit = async (item: MudType, key: string, newValue: any) => {
    const value = key === 'density' && newValue ? Number(newValue) : newValue;
    await CRUDHelper.update('mudTypes', item.id, { [key]: value }, user?.uid, user?.email || '');
  };

  // ===== Phase Oleosa CRUD Operations =====
  const handlePhaseOleosaSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('phaseOleosa', {
      name: data.name,
      density: Number(data.density),
      cost: data.cost ? Number(data.cost) : 0,
      active: true
    }, user?.uid, user?.email || '');
  };

  const handlePhaseOleosaToggle = async (item: PhaseOleosa) => {
    await CRUDHelper.toggleBoolean(
      'phaseOleosa',
      item.id,
      'active',
      item.active,
      user?.uid,
      user?.email || ''
    );
  };

  const handlePhaseOleosaDelete = async (item: PhaseOleosa) => {
    if (window.confirm(`¿Estás seguro de eliminar la fase oleosa "${item.name}"?`)) {
      await CRUDHelper.delete('phaseOleosa', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handlePhaseOleosaEdit = async (item: PhaseOleosa, key: string, newValue: any) => {
    const value = (key === 'density' || key === 'cost') ? Number(newValue) : newValue;
    await CRUDHelper.update('phaseOleosa', item.id, { [key]: value }, user?.uid, user?.email || '');
  };

  // ===== Densificante CRUD Operations =====
  const handleDensificanteSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('densificantes', {
      material: data.material,  // Fixed: use 'material' instead of 'name'
      density: Number(data.density),
      active: true
    }, user?.uid, user?.email || '');
  };

  const handleDensificanteToggle = async (item: Densificante) => {
    await CRUDHelper.toggleBoolean(
      'densificantes',
      item.id,
      'active',
      item.active,
      user?.uid,
      user?.email || ''
    );
  };

  const handleDensificanteDelete = async (item: Densificante) => {
    if (window.confirm(`¿Estás seguro de eliminar el densificante "${item.material}"?`)) {
      await CRUDHelper.delete('densificantes', item.id, user?.uid, user?.email || '', item);
    }
  };

  const handleDensificanteEdit = async (item: Densificante, key: string, newValue: any) => {
    const value = (key === 'density' || key === 'cost') ? Number(newValue) : newValue;
    await CRUDHelper.update('densificantes', item.id, { [key]: value }, user?.uid, user?.email || '');
  };

  // ===== Densidad LGS CRUD Operations =====
  const handleDensidadLGSSubmit = async (data: Record<string, any>) => {
    await CRUDHelper.create('densidadesLGS', {
      value: Number(data.value),
      active: true
    }, user?.uid, user?.email || '');
  };

  const handleDensidadLGSToggle = async (item: DensidadLGS) => {
    await CRUDHelper.toggleBoolean(
      'densidadesLGS',
      item.id!,
      'active',
      item.active,
      user?.uid,
      user?.email || ''
    );
  };

  const handleDensidadLGSDelete = async (item: DensidadLGS) => {
    if (window.confirm(`¿Estás seguro de eliminar la densidad LGS "${item.value}" g/cm³?`)) {
      await CRUDHelper.delete('densidadesLGS', item.id!, user?.uid, user?.email || '', item);
    }
  };

  const handleDensidadLGSEdit = async (item: DensidadLGS, key: string, newValue: any) => {
    const value = key === 'value' ? Number(newValue) : newValue;
    await CRUDHelper.update('densidadesLGS', item.id!, { [key]: value }, user?.uid, user?.email || '');
  };

  // ===== Container Measures Operations =====
  const handleContainerMeasuresUpdate = async (data: ContainerMeasures) => {
    const docId = data.id || 'global';
    await CRUDHelper.update('containerMeasures', docId, {
      altura_max: Number(data.altura_max),
      ancho: Number(data.ancho),
      base: Number(data.base),
      largo: Number(data.largo),
      arriba: Number(data.arriba)
    }, user?.uid, user?.email || '');
  };

  // ===== Quick Setup Functions =====
  const handleEquipmentBrandsQuickSetup = async () => {
    const standardBrands = [
      { equipmentType: 'zaranda', brand: 'SWACO', model: 'Mongoose' },
      { equipmentType: 'zaranda', brand: 'DERRICK', model: 'FLC-2000' },
      { equipmentType: 'desarenador', brand: 'SWACO', model: 'MD-3' },
      { equipmentType: 'desilter', brand: 'DERRICK', model: 'DE-1000' },
      { equipmentType: 'secadora', brand: 'SWACO', model: 'VG-Dryer' },
      { equipmentType: 'decanter', brand: 'ALFA LAVAL', model: 'ALDEC-40' }
    ];

    for (const brand of standardBrands) {
      await CRUDHelper.create('equipmentBrands', {
        ...brand,
        active: true
      }, user?.uid, user?.email || '');
    }
  };

  const handleDischargeTypesQuickSetup = async () => {
    const standardTypes = [
      {
        type: 'Contenedor',
        description: 'Descarga directa a contenedor (Zarandas, Secadoras, Decanters)',
        applicableEquipment: ['zaranda1', 'zaranda2', 'zaranda3', 'secadora1', 'secadora2', 'secadora3', 'decanter1', 'decanter2', 'decanter3']
      },
      {
        type: 'Secadora',
        description: 'Descarga procesada por secadora (solo Desarenador y Desilter)',
        applicableEquipment: ['desarenador', 'desilter']
      }
    ];

    for (const discharge of standardTypes) {
      await CRUDHelper.create('solidsDischargeTypes', {
        ...discharge,
        active: true
      }, user?.uid, user?.email || '');
    }
  };

  const handleScreenTypesQuickSetup = async () => {
    const standardScreens = [
      { mesh: 40, description: 'Malla API RP 13C - 40 mesh', equipmentApplicable: ['zaranda1', 'zaranda2', 'zaranda3'] },
      { mesh: 60, description: 'Malla API RP 13C - 60 mesh', equipmentApplicable: ['zaranda1', 'zaranda2'] },
      { mesh: 80, description: 'Malla API RP 13C - 80 mesh', equipmentApplicable: ['zaranda2', 'zaranda3'] },
      { mesh: 100, description: 'Malla API RP 13C - 100 mesh', equipmentApplicable: ['zaranda2', 'zaranda3'] },
      { mesh: 120, description: 'Malla API RP 13C - 120 mesh', equipmentApplicable: ['zaranda3'] },
      { mesh: 150, description: 'Malla API RP 13C - 150 mesh', equipmentApplicable: ['zaranda3'] },
      { mesh: 200, description: 'Malla API RP 13C - 200 mesh', equipmentApplicable: ['zaranda3'] }
    ];

    for (const screen of standardScreens) {
      await CRUDHelper.create('screenTypes', {
        ...screen,
        active: true
      }, user?.uid, user?.email || '');
    }
  };

  const handleEmpresasQuickSetup = async () => {
    const standardEmpresas = [
      { nombre: 'MARBAR', codigo: 'marbar' },
      { nombre: 'TECPETROL', codigo: 'tecpetrol' },
      { nombre: 'YPF', codigo: 'ypf' },
      { nombre: 'SHELL', codigo: 'shell' },
      { nombre: 'TOTAL AUSTRAL', codigo: 'total' },
      { nombre: 'WINTERSHALL', codigo: 'wintershall' }
    ];

    for (const empresa of standardEmpresas) {
      await CRUDHelper.create('empresas', {
        ...empresa,
        active: true
      }, user?.uid, user?.email || '');
    }
  };

  return {
    // Equipment Brand Operations
    handleEquipmentBrandSubmit,
    handleEquipmentBrandToggle,
    handleEquipmentBrandDelete,
    handleEquipmentBrandEdit,

    // Discharge Type Operations
    handleDischargeTypeSubmit,
    handleDischargeTypeToggle,
    handleDischargeTypeDelete,
    handleDischargeTypeEdit,

    // Screen Type Operations
    handleScreenTypeSubmit,
    handleScreenTypeToggle,
    handleScreenTypeDelete,
    handleScreenTypeEdit,

    // Empresa Operations
    handleEmpresaSubmit,
    handleEmpresaToggle,
    handleEmpresaDelete,
    handleEmpresaEdit,

    // Operadora Operations
    handleOperadoraSubmit,
    handleOperadoraToggle,
    handleOperadoraDelete,
    handleOperadoraEdit,

    // Equipo Operations
    handleEquipoSubmit,
    handleEquipoToggle,
    handleEquipoDelete,
    handleEquipoEdit,

    // Pad Operations
    handlePadSubmit,
    handlePadDelete,
    handlePadEdit,

    // Pozo Operations
    handlePozoSubmit,
    handlePozoDelete,
    handlePozoEdit,

    // Tramo Operations
    handleTramoSubmit,
    handleTramoDelete,
    handleTramoEdit,

    // Trepano Operations
    handleTrepanoSubmit,
    handleTrepanoToggle,
    handleTrepanoDelete,
    handleTrepanoEdit,

    // Pileta Tipo Operations
    handlePiletaTipoSubmit,
    handlePiletaTipoDelete,
    handlePiletaTipoEdit,

    // Pileta Equipo Operations
    handlePiletaEquipoSubmit,
    handlePiletaEquipoToggle,
    handlePiletaEquipoDelete,
    handlePiletaEquipoEdit,

    // Material Operations
    handleMaterialSubmit,
    handleMaterialToggle,
    handleMaterialDelete,
    handleMaterialEdit,

    // Servicio Tipo Operations
    handleServicioTipoSubmit,
    handleServicioTipoToggle,
    handleServicioTipoDelete,
    handleServicioTipoEdit,

    // Daily Activity Operations
    handleDailyActivitySubmit,
    handleDailyActivityToggle,
    handleDailyActivityDelete,
    handleDailyActivityEdit,

    // Mud Type Operations
    handleMudTypeSubmit,
    handleMudTypeToggle,
    handleMudTypeDelete,
    handleMudTypeEdit,

    // Phase Oleosa Operations
    handlePhaseOleosaSubmit,
    handlePhaseOleosaToggle,
    handlePhaseOleosaDelete,
    handlePhaseOleosaEdit,

    // Densificante Operations
    handleDensificanteSubmit,
    handleDensificanteToggle,
    handleDensificanteDelete,
    handleDensificanteEdit,

    // Densidad LGS Operations
    handleDensidadLGSSubmit,
    handleDensidadLGSToggle,
    handleDensidadLGSDelete,
    handleDensidadLGSEdit,

    // Container Measures Operations
    handleContainerMeasuresUpdate,

    // Quick Setup Functions
    handleEquipmentBrandsQuickSetup,
    handleDischargeTypesQuickSetup,
    handleScreenTypesQuickSetup,
    handleEmpresasQuickSetup
  };
};
